/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useState } from 'react';
import { BarcodeScannerModal } from '../../shared/ui/BarcodeScannerModal';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { PendingTransfersModal } from '../components/PendingTransfersModal';
import { MovementHistoryModal } from '../components/MovementHistoryModal';
import { BranchActivityModal } from '../components/BranchActivityModal';
import { StockDetailModal } from '../components/StockDetailModal';
import { InventoryStockActionModals } from '../components/InventoryStockActionModals';
import { CorrectCostModal } from '../components/CorrectCostModal';
import { PhysicalCountModal } from '../components/PhysicalCountModal';
import { TransferProductPickerModal } from '../components/TransferProductPickerModal';
import { AddStockModal } from '../components/AddStockModal';
import { BulkCoefficientModal } from '../components/BulkCoefficientModal';
import { CsvImportExportModal } from '../../shared/csv/CsvImportExportModal';
import { createStockCsvAdapter } from '../infra/stockCsvAdapter';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useProductVariantsEnabled } from '../../catalog/app-api';
import type { InventoryScreenViewModel } from './useInventoryScreen';

type InventoryScreenModalsProps = {
  inventoryVm: InventoryScreenViewModel;
  layoutWide: boolean;
  csvModalVisible: boolean;
  onCloseCsvModal: () => void;
};

export const InventoryScreenModals = ({
  inventoryVm,
  layoutWide,
  csvModalVisible,
  onCloseCsvModal,
}: InventoryScreenModalsProps) => {
  const user = useAuthStore((s) => s.user);
  // #709: gatear los selectores de variante en inventario con la capability.
  const variantsEnabled = useProductVariantsEnabled();

  // Guard de descarte para el modal de coeficiente en lote (vive en catálogo,
  // es un Modal crudo): si hay alcance/%/vista previa cargados, confirmar antes
  // de cerrar. Mismo patrón que el dirty-guard del form de catálogo.
  const { confirm: confirmBulkDiscard, ConfirmModal: BulkDiscardConfirmModal } = useOperationalConfirm();
  const [bulkDiscardOpen, setBulkDiscardOpen] = useState(false);
  const bulkDirty =
    inventoryVm.bulkPhysical.bulkPercentInput.trim() !== '' ||
    inventoryVm.bulkPhysical.bulkSearch.trim() !== '' ||
    inventoryVm.bulkPhysical.bulkCategoryScope.mode !== 'all' ||
    inventoryVm.bulkPhysical.bulkPreview.length > 0;
  const closeBulkCoeffModal = useCallback(() => {
    inventoryVm.bulkPhysical.setBulkCoeffVisible(false);
  }, [inventoryVm.bulkPhysical]);
  const guardedCloseBulkCoeff = useCallback(() => {
    if (inventoryVm.bulkPhysical.bulkSaving) return;
    // Si el confirm ya está arriba, Escape/back lo cierra a él.
    if (bulkDiscardOpen) return;
    if (!bulkDirty) {
      closeBulkCoeffModal();
      return;
    }
    setBulkDiscardOpen(true);
    void confirmBulkDiscard({
      title: 'Descartar actualización en lote',
      message: 'Hay datos cargados sin aplicar. Si cerrás, se pierden.',
      confirmLabel: 'Descartar',
      destructive: true,
      iconName: 'trash-outline',
    }).then((confirmed) => {
      setBulkDiscardOpen(false);
      if (confirmed) closeBulkCoeffModal();
    });
  }, [bulkDiscardOpen, bulkDirty, closeBulkCoeffModal, confirmBulkDiscard, inventoryVm.bulkPhysical.bulkSaving]);

  return (
    <>
      <TransferProductPickerModal
        visible={inventoryVm.transfers.transferPickerVisible}
        searchQuery={inventoryVm.transfers.transferPickerSearch}
        searchQueryDebounced={inventoryVm.transfers.transferPickerSearchDebounced}
        onSearchChange={inventoryVm.transfers.setTransferPickerSearch}
        stocks={inventoryVm.transfers.transferPickerStocks}
        loading={inventoryVm.transfers.transferPickerLoading}
        loadingMore={inventoryVm.transfers.transferPickerLoadingMore}
        hasMore={inventoryVm.transfers.transferPickerHasMore}
        onLoadMore={inventoryVm.transfers.loadMoreTransferPicker}
        onSelect={inventoryVm.transfers.selectStockAndOpenTransferModal}
        onClose={inventoryVm.transfers.closeTransferPicker}
        onOpenScanner={() => inventoryVm.transfers.setTransferPickerScannerVisible(true)}
        showBarcodeCameraButton={inventoryVm.global.barcodeAllowCamera}
        onSearchSubmitEditing={
          inventoryVm.global.barcodeAllowHardware ? inventoryVm.transfers.handleTransferPickerSearchHardwareSubmit : undefined
        }
        autoFocusSearchOnWeb={false}
      />

      <InventoryStockActionModals
        adjustModalVisible={inventoryVm.adjustment.adjustModalVisible}
        adjustStockRow={inventoryVm.adjustment.adjustStockRow}
        adjustDelta={inventoryVm.adjustment.adjustDelta}
        adjustReason={inventoryVm.adjustment.adjustReason}
        adjustSaving={inventoryVm.adjustment.adjustSaving}
        onAdjustDeltaChange={inventoryVm.adjustment.setAdjustDelta}
        onAdjustReasonChange={inventoryVm.adjustment.setAdjustReason}
        onCloseAdjust={inventoryVm.adjustment.closeAdjustModal}
        onApplyAdjustment={inventoryVm.adjustment.applyAdjustment}
        onAdjustStockRowChange={inventoryVm.adjustment.setAdjustStockRow}
        transferModalVisible={inventoryVm.transfers.transferModalVisible}
        selectedStock={inventoryVm.transfers.selectedStock}
        destinationBranches={inventoryVm.transfers.destinationBranches}
        destinationBranchId={inventoryVm.transfers.destinationBranchId}
        transferQuantity={inventoryVm.transfers.transferQuantity}
        transferWaitForReceive={inventoryVm.transfers.transferWaitForReceive}
        isSubmittingTransfer={inventoryVm.transfers.isSubmittingTransfer}
        transferError={inventoryVm.transfers.transferError}
        onDestinationBranchChange={inventoryVm.transfers.setDestinationBranchId}
        onTransferQuantityChange={inventoryVm.transfers.setTransferQuantity}
        onToggleTransferWaitForReceive={() => inventoryVm.transfers.setTransferWaitForReceive(!inventoryVm.transfers.transferWaitForReceive)}
        onCloseTransfer={inventoryVm.transfers.closeTransferModal}
        onConfirmTransfer={inventoryVm.transfers.handleConfirmTransfer}
        onTransferStockRowChange={inventoryVm.transfers.setSelectedStock}
        variantsEnabled={variantsEnabled}
      />

      <CorrectCostModal
        visible={inventoryVm.adjustment.costCorrectModalVisible}
        stock={inventoryVm.adjustment.costCorrectStockRow}
        costInput={inventoryVm.adjustment.costCorrectInput}
        saving={inventoryVm.adjustment.costCorrectSaving}
        onCostInputChange={inventoryVm.adjustment.setCostCorrectInput}
        onClose={inventoryVm.adjustment.closeCostCorrectModal}
        onSave={inventoryVm.adjustment.applyCostCorrect}
      />

      <StockDetailModal
        visible={inventoryVm.uiState.detailVisible}
        stock={inventoryVm.uiState.detailStock}
        canEdit={inventoryVm.global.canEdit}
        canAdjust={inventoryVm.global.canAdjust}
        canTransfer={inventoryVm.global.canTransfer}
        canViewValuation={inventoryVm.global.canViewValuation}
        layoutWide={layoutWide}
        onClose={inventoryVm.uiState.closeDetailModal}
        onIngress={inventoryVm.addStock.openAddStockForStockRow}
        onAdjust={inventoryVm.adjustment.openAdjustModal}
        onWriteOffExpired={inventoryVm.adjustment.openExpiryWriteOffModal}
        onCorrectCost={inventoryVm.adjustment.openCostCorrectModal}
        onTransfer={inventoryVm.transfers.openTransferModal}
        onHistory={inventoryVm.historyActivity.openHistoryModal}
        variantsEnabled={variantsEnabled}
      />

      <AddStockModal
        visible={inventoryVm.addStock.addStockVisible}
        productQuery={inventoryVm.addStock.productQuery}
        productQueryDebounced={inventoryVm.addStock.productQueryDebounced}
        entryScanFeedback={inventoryVm.addStock.entryScanFeedback}
        entrySuggestion={inventoryVm.addStock.entrySuggestion}
        productResults={inventoryVm.addStock.productResults}
        productLoadingMore={inventoryVm.addStock.productLoadingMore}
        productHasMore={inventoryVm.addStock.productHasMore}
        entryQuantity={inventoryVm.addStock.entryQuantity}
        entryUnitCost={inventoryVm.addStock.entryUnitCost}
        entryReason={inventoryVm.addStock.entryReason}
        entryMinQuantity={inventoryVm.addStock.entryMinQuantity}
        entryExpiry={inventoryVm.addStock.entryExpiry}
        entrySupplierId={inventoryVm.addStock.entrySupplierId}
        entryLotNumber={inventoryVm.addStock.entryLotNumber}
        onEntryLotNumberChange={inventoryVm.addStock.setEntryLotNumber}
        entryVariantId={inventoryVm.addStock.entryVariantId}
        onEntryVariantIdChange={inventoryVm.addStock.setEntryVariantId}
        availableVariants={inventoryVm.addStock.availableVariants}
        savingEntry={inventoryVm.addStock.savingEntry}
        stocks={inventoryVm.stockList.stocks}
        lastUnitCost={inventoryVm.addStock.lastUnitCost}
        onClose={inventoryVm.addStock.closeAddStockModal}
        onProductQueryChange={inventoryVm.addStock.setProductQuery}
        onOpenScanner={() => inventoryVm.addStock.setEntryScannerVisible(true)}
        showBarcodeCameraButton={inventoryVm.global.barcodeAllowCamera}
        onProductQuerySubmitEditing={
          inventoryVm.global.barcodeAllowHardware ? inventoryVm.addStock.handleEntryProductQueryHardwareSubmit : undefined
        }
        autoFocusProductQueryOnWeb={false}
        onOpenCatalog={inventoryVm.global.openCatalog}
        onSelectProduct={inventoryVm.addStock.handleSelectProduct}
        onClearSelectedProduct={inventoryVm.addStock.clearSelectedProduct}
        onEntryQuantityChange={inventoryVm.addStock.setEntryQuantity}
        onEntryUnitCostChange={inventoryVm.addStock.setEntryUnitCost}
        onEntryReasonChange={inventoryVm.addStock.setEntryReason}
        onEntryMinQuantityChange={inventoryVm.addStock.setEntryMinQuantity}
        onEntryExpiryChange={inventoryVm.addStock.setEntryExpiry}
        onEntrySupplierIdChange={inventoryVm.addStock.setEntrySupplierId}
        onLoadMoreProducts={inventoryVm.addStock.loadMoreProducts}
        onSave={inventoryVm.addStock.handleAddStock}
        selectedProduct={inventoryVm.addStock.selectedProduct}
        suppliers={inventoryVm.addStock.suppliers}
      />

      <MovementHistoryModal
        visible={inventoryVm.historyActivity.historyVisible}
        layoutWide={layoutWide}
        selectedStock={inventoryVm.historyActivity.historySelectedStock}
        movementHistory={inventoryVm.historyActivity.movementHistory}
        historyLoading={inventoryVm.historyActivity.historyLoading}
        historyLoadingMore={inventoryVm.historyActivity.historyLoadingMore}
        historyHasMore={inventoryVm.historyActivity.historyHasMore}
        onLoadMore={inventoryVm.historyActivity.loadMoreHistory}
        onClose={inventoryVm.historyActivity.closeHistoryModal}
        actorLabel={inventoryVm.global.actorLabel}
        reasonOptions={inventoryVm.historyActivity.historyReasonOptions}
        reasonFilter={inventoryVm.historyActivity.historyReasonFilter}
        onReasonFilterChange={inventoryVm.historyActivity.setHistoryReasonFilter}
        fromDate={inventoryVm.historyActivity.historyFromDate}
        toDate={inventoryVm.historyActivity.historyToDate}
        onFromDateChange={inventoryVm.historyActivity.setHistoryFromDate}
        onToDateChange={inventoryVm.historyActivity.setHistoryToDate}
        onExportCsv={() => void inventoryVm.historyActivity.exportMovementHistoryCsv()}
        exportingCsv={inventoryVm.historyActivity.exportingHistory}
        reasonLabelFn={(reason) => inventoryVm.global.getStockMovementReasonLabel(reason ?? null)}
        onViewSale={(m) => void inventoryVm.historyActivity.openSaleFromMovement(m)}
      />

      <BranchActivityModal
        visible={inventoryVm.historyActivity.activityModalVisible}
        branchActivity={inventoryVm.historyActivity.branchActivity}
        branches={inventoryVm.global.branches}
        layoutWide={layoutWide}
        onClose={inventoryVm.historyActivity.closeActivityModal}
        onOpenProductStock={inventoryVm.historyActivity.openStockFromBranchActivity}
      />

      <PendingTransfersModal
        visible={inventoryVm.transfers.pendingTransfersModalVisible}
        pendingTransfers={inventoryVm.transfers.pendingTransfers}
        branches={inventoryVm.global.branches}
        canEdit={inventoryVm.global.canEdit}
        processingTransferId={inventoryVm.transfers.processingTransferId}
        onClose={inventoryVm.transfers.closePendingTransfersModal}
        onReceive={inventoryVm.transfers.handleReceiveTransfer}
        onReject={inventoryVm.transfers.handleRejectTransfer}
      />

      <BulkCoefficientModal
        visible={inventoryVm.bulkPhysical.bulkCoeffVisible}
        kicker="Stock"
        bulkCategoryScope={inventoryVm.bulkPhysical.bulkCategoryScope}
        onBulkCategoryScopeChange={inventoryVm.bulkPhysical.setBulkCategoryScope}
        bulkSearch={inventoryVm.bulkPhysical.bulkSearch}
        bulkPercentInput={inventoryVm.bulkPhysical.bulkPercentInput}
        bulkApplyTo={inventoryVm.bulkPhysical.bulkApplyTo}
        bulkPreview={inventoryVm.bulkPhysical.bulkPreview}
        bulkApplyTotal={inventoryVm.bulkPhysical.bulkApplyTotal}
        categoryOptions={inventoryVm.bulkPhysical.bulkCategoryOptions}
        bulkPreviewLoading={inventoryVm.bulkPhysical.bulkPreviewLoading}
        bulkSaving={inventoryVm.bulkPhysical.bulkSaving}
        bulkSaveProgress={inventoryVm.bulkPhysical.bulkSaveProgress}
        onClose={guardedCloseBulkCoeff}
        onSearchChange={inventoryVm.bulkPhysical.setBulkSearch}
        onOpenScanner={() => inventoryVm.bulkPhysical.setBulkScannerVisible(true)}
        showBarcodeCameraButton={inventoryVm.global.barcodeAllowCamera}
        onSearchSubmitEditing={
          inventoryVm.global.barcodeAllowHardware ? inventoryVm.bulkPhysical.handleBulkSearchHardwareSubmit : undefined
        }
        autoFocusSearchOnWeb={false}
        onPercentChange={inventoryVm.bulkPhysical.setBulkPercentInput}
        onApplyToChange={inventoryVm.bulkPhysical.setBulkApplyTo}
        onPreview={inventoryVm.bulkPhysical.loadBulkPreview}
        onConfirm={inventoryVm.bulkPhysical.applyBulkCoefficient}
        onClearPreview={() => {
          inventoryVm.bulkPhysical.setBulkPreview([]);
          inventoryVm.bulkPhysical.setBulkApplyTotal(0);
        }}
        bulkPriceRounding={inventoryVm.bulkPhysical.bulkPriceRounding}
        onBulkPriceRoundingChange={inventoryVm.bulkPhysical.setBulkPriceRounding}
      />
      {BulkDiscardConfirmModal}

      {inventoryVm.global.barcodeAllowCamera ? (
        <BarcodeScannerModal
          visible={inventoryVm.bulkPhysical.bulkScannerVisible}
          title="Escanear código de barras"
          subtitle="Leé el código para aplicar el porcentaje a ese producto."
          onClose={() => inventoryVm.bulkPhysical.setBulkScannerVisible(false)}
          onCodeScanned={inventoryVm.bulkPhysical.handleBulkScan}
        />
      ) : null}

      <PhysicalCountModal
        visible={inventoryVm.bulkPhysical.physicalCountVisible}
        rows={inventoryVm.bulkPhysical.physicalCountRows}
        loading={inventoryVm.bulkPhysical.physicalCountLoading}
        saving={inventoryVm.bulkPhysical.physicalCountSaving}
        onClose={() => inventoryVm.bulkPhysical.setPhysicalCountVisible(false)}
        onChangeQty={inventoryVm.bulkPhysical.setPhysicalCountedQty}
        onApply={inventoryVm.bulkPhysical.applyPhysicalCountDifferences}
        onScanCode={inventoryVm.bulkPhysical.resolvePhysicalCountScanFilter}
        showBarcodeCameraButton={inventoryVm.global.barcodeAllowCamera}
      />

      {inventoryVm.global.barcodeAllowCamera ? (
        <BarcodeScannerModal
          visible={inventoryVm.uiState.searchScannerVisible}
          title="Escanear en stock"
          subtitle="Leé el código para ubicarlo rápido en esta sucursal."
          onClose={() => inventoryVm.uiState.setSearchScannerVisible(false)}
          onCodeScanned={inventoryVm.global.handleInventorySearchScan}
        />
      ) : null}

      {inventoryVm.global.barcodeAllowCamera ? (
        <BarcodeScannerModal
          visible={inventoryVm.addStock.entryScannerVisible}
          title="Escanear para ingreso"
          subtitle="Leé el código para elegirlo en el ingreso."
          onClose={() => inventoryVm.addStock.setEntryScannerVisible(false)}
          onCodeScanned={inventoryVm.addStock.handleEntryScan}
          requireConfirmation
        />
      ) : null}

      {inventoryVm.global.barcodeAllowCamera ? (
        <BarcodeScannerModal
          visible={inventoryVm.transfers.transferPickerScannerVisible}
          title="Escanear para transferencia"
          subtitle="Leé el código para filtrar el producto."
          onClose={() => inventoryVm.transfers.setTransferPickerScannerVisible(false)}
          onCodeScanned={inventoryVm.transfers.handleTransferPickerScan}
        />
      ) : null}

      <CsvImportExportModal
        visible={csvModalVisible}
        onClose={onCloseCsvModal}
        mode="stock"
        kicker="Stock"
        branchId={inventoryVm.global.currentBranchId ?? null}
        branchLabel={
          inventoryVm.global.branches.find((b) => b.id === inventoryVm.global.currentBranchId)?.name
        }
        canEdit={inventoryVm.global.canEdit}
        performedByUserId={user?.id ?? null}
        // «Exportar listado» absorbido acá: segundo alcance con los filtros actuales.
        filteredExportLabel="Listado filtrado"
        onDownloadFilteredExport={() => void inventoryVm.stockList.exportStockListCsv()}
        onApplied={() => void inventoryVm.global.loadFn({ force: true })}
        adapter={createStockCsvAdapter(inventoryVm.global.currentBranchId ?? '', inventoryVm.global.branches.find((b) => b.id === inventoryVm.global.currentBranchId)?.name, user?.id ?? null)}
      />
    </>
  );
};
